import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChatPromptDto, UpdateChatPromptDto } from "@/dto/ChatPromptDto";
import { superChatPromptApi } from "@/apis/superChatPrompt";

/**
 * 프롬프트 상세 페이지 훅
 *
 * @description 프롬프트 상세 정보 조회, 수정, 삭제 로직을 관리하는 커스텀 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
function usePromptDetail(promptId: number) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<ChatPromptDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * 프롬프트 상세 정보 조회
   */
  const fetchPromptDetail = async () => {
    if (!promptId) return;

    setIsLoading(true);
    try {
      console.log("[FETCH_PROMPT_DETAIL]", {
        action: "start",
        promptId,
        timestamp: new Date().toISOString(),
      });

      const response = await superChatPromptApi.getDetail(promptId);
      setPrompt(response);

      console.log("[FETCH_PROMPT_DETAIL]", {
        action: "success",
        promptId,
        promptKey: response.promptKey,
      });
    } catch (error: any) {
      console.error("[FETCH_PROMPT_DETAIL]", {
        action: "error",
        error: error.message,
        promptId,
      });

      alert(`프롬프트 조회 실패: ${error.message}`);
      navigate("/chat/content");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 프롬프트 수정 핸들러
   */
  const handlePromptUpdate = async (updateData: UpdateChatPromptDto) => {
    if (!prompt) return;

    setIsLoading(true);
    try {
      console.log("[UPDATE_PROMPT]", {
        action: "start",
        promptId: prompt.promptId,
        updateData,
      });

      const updatedPrompt = await superChatPromptApi.update(
        prompt.promptId,
        updateData
      );
      setPrompt(updatedPrompt);

      console.log("[UPDATE_PROMPT]", {
        action: "success",
        promptId: prompt.promptId,
      });

      alert("프롬프트가 성공적으로 수정되었습니다.");
    } catch (error: any) {
      console.error("[UPDATE_PROMPT]", {
        action: "error",
        error: error.message,
        promptId: prompt.promptId,
      });

      alert(`프롬프트 수정 실패: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 프롬프트 삭제 핸들러
   */
  const handlePromptDelete = async () => {
    if (!prompt) return;

    const confirmed = confirm(
      `정말로 "${prompt.promptKey}" 프롬프트를 삭제하시겠습니까?`
    );
    if (!confirmed) return;

    setIsLoading(true);
    try {
      console.log("[DELETE_PROMPT]", {
        action: "start",
        promptId: prompt.promptId,
        promptKey: prompt.promptKey,
      });

      await superChatPromptApi.delete(prompt.promptId);

      console.log("[DELETE_PROMPT]", {
        action: "success",
        promptId: prompt.promptId,
      });

      alert("프롬프트가 성공적으로 삭제되었습니다.");
      navigate("/chat/content");
    } catch (error: any) {
      console.error("[DELETE_PROMPT]", {
        action: "error",
        error: error.message,
        promptId: prompt.promptId,
      });

      alert(`프롬프트 삭제 실패: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchPromptDetail();
  }, [promptId]);

  return {
    prompt,
    isLoading,
    handlePromptUpdate,
    handlePromptDelete,
    refetch: fetchPromptDetail,
  };
}

export default usePromptDetail;

